import { Ng2StateDeclaration, StatesModule, Transition } from '@uirouter/angular';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { ApplicationSettingsType } from '@fi-shared/types/application-settings-type';
import { AccessRightType } from '@fi-shared/types/access-right-type';
import { PageTitleService } from '@fi-app/utilities/fi-services/page-title.service';
import { FiAgreementEditComponent } from '@fi-app/agreement/agreement-edit/agreement-edit.component';
import { AgreementService } from '@fi-app/agreement/agreement.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { AgreementModel } from '@fi-shared/fi-agreement/agreement.model';
import { FiAgreementViewVersionComponent } from '@fi-app/agreement/agreement-view-version/agreement-view-version.component';
import { FiAgreementViewAcceptedComponent } from '@fi-app/agreement/agreement-view-accepted/agreement-view-accepted.component';
import { AgreementViewAcceptedViewComponent } from '@fi-app/agreement/agreement-view-accepted-view/agreement-view-accepted-view.component';
import { FiAgreementViewComponent } from '@fi-app/agreement/agreement-view/agreement-view.component';
import { FiAgreementsListComponent } from '@fi-app/agreement/agreements-list/agreements-list.component';
import { FiAbstractStateComponent } from '@fi-shared/fi-state-proxy/fi-abstract-state.component';
import { FiAgreementViewAgreementComponent } from '@fi-app/agreement/agreement-view-agreement/agreement-view-agreement.component';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';

const AgreementState: Ng2StateDeclaration = {
    name: FiStateNames.Agreement._name,
    url: '/Agreement',
    parent: FiStateNames.GlobalSettings,
    component: FiAbstractStateComponent,
    abstract: true,
};

export function AgreementListPageTitle(ccmPageTitle: PageTitleService) {
    return ccmPageTitle.setTitleByItem(ObjectTypes.Agreement);
}

const AgreementListState: Ng2StateDeclaration = {
    name: FiStateNames.Agreement.List,
    url: '/?:sortColumn&:sortDirection&:offset',
    component: FiAgreementsListComponent,
    reloadOnSearch: false,
    resolve: {
        pageTitle: [PageTitleService, AgreementListPageTitle],
    },
};

export function AgreementViewAgreement(stateService: FiStateService, transition, AgreementService: AgreementService) {
    return AgreementService.GetAgreement(stateService.getParams(transition).Id, true);
}

export function AgreementViewPageTitle(ccmPageTitle: PageTitleService, agreement: AgreementModel) {
    ccmPageTitle.setTitleByItem(agreement);
}

export function AgreementViewLanguageId(stateService: FiStateService, transition) {
    return ~~stateService.getParams(transition).languageId;
}

const AgreementViewState: Ng2StateDeclaration = {
    name: FiStateNames.Agreement.View._name,
    url: '/{Id:[0-9]+}?:languageId',
    resolve: {
        agreement: [FiStateService, Transition, AgreementService, AgreementViewAgreement],
        pageTitle: [PageTitleService, 'agreement', AgreementViewPageTitle],
        languageId: [FiStateService, Transition, AgreementViewLanguageId],
    },
    views: {
        '@': {
            component: FiAgreementViewComponent,
        },
        '@Agreement.View': {
            component: FiAgreementViewAgreementComponent,
        },
    },
};

const AgreementViewAcceptedState: Ng2StateDeclaration = {
    name: FiStateNames.Agreement.View.Accepted._name,
    url: '/Accepted',
    abstract: true,
    component: FiAbstractStateComponent,
};

export function AgreementViewAcceptedPageTitle(ccmPageTitle: PageTitleService, agreement: AgreementModel) {
    ccmPageTitle.setTitleByItem(agreement);
}
const AgreementViewAcceptedListState: Ng2StateDeclaration = {
    name: FiStateNames.Agreement.View.Accepted.List,
    url: '/',
    views: {
        '@Agreement.View': {
            component: FiAgreementViewAcceptedComponent,
        },
    },
    resolve: {
        pageTitle: [PageTitleService, 'agreement', AgreementViewAcceptedPageTitle],
    },
};

export function AgreementViewViewAcceptedAgreementUserLog(
    stateService: FiStateService,
    transition: Transition,
    AgreementService: AgreementService,
) {
    return AgreementService.GetAgreementUserLog(
        stateService.getParams(transition).Id,
        stateService.getParams(transition).AgreementUserLogId,
    );
}
const AgreementViewViewAcceptedState: Ng2StateDeclaration = {
    name: FiStateNames.Agreement.View.Accepted.View,
    url: '/{AgreementUserLogId:[0-9]+}',
    views: {
        '@Agreement.View': {
            component: AgreementViewAcceptedViewComponent,
        },
    },
    resolve: {
        agreementUserLog: [FiStateService, Transition, AgreementService, AgreementViewViewAcceptedAgreementUserLog],
    },
};

export function AgreementViewVersionPageTitle(ccmPageTitle: PageTitleService, agreement: AgreementModel) {
    return ccmPageTitle.setTitleByItem(agreement);
}
const AgreementViewVersionState: Ng2StateDeclaration = {
    name: FiStateNames.Agreement.View.Version,
    url: '/Version/{Version:[0-9]+}',
    views: {
        '@': {
            component: FiAgreementViewVersionComponent,
        },
    },
    resolve: {
        pageTitle: [PageTitleService, 'agreement', AgreementViewVersionPageTitle],
    },
};

export function AgreementEditAgreement(
    AgreementService: AgreementService,
    stateService: FiStateService,
    transition: Transition,
) {
    return AgreementService.GetAgreement(stateService.getParams(transition).Id);
}

export function AgreementEditLanguageId(stateService: FiStateService, transition: Transition) {
    return ~~stateService.getParams(transition).languageId;
}

export function AgreementEditPageTitle(ccmPageTitle: PageTitleService, agreement: AgreementModel) {
    ccmPageTitle.setTitleByItem(agreement);
}

const AgreementEditState: Ng2StateDeclaration = {
    name: FiStateNames.Agreement.Edit,
    url: '/{Id:[0-9]+}/Edit?:languageId',
    resolve: {
        agreement: [AgreementService, FiStateService, Transition, AgreementEditAgreement],
        languageId: [FiStateService, Transition, AgreementEditLanguageId],
        pageTitle: [PageTitleService, 'agreement', AgreementEditPageTitle],
    },
    views: {
        '@': {
            component: FiAgreementEditComponent,
        },
    },
};

export const AgreementStates: StatesModule = {
    states: [
        AgreementState,
        AgreementListState,
        AgreementViewState,
        AgreementViewAcceptedState,
        AgreementViewAcceptedListState,
        AgreementViewViewAcceptedState,
        AgreementViewVersionState,
        AgreementEditState,
    ],
};

// register access rules for agreement module
export function registerAgreementStateRules(stateAccessCheckService: StateAccessCheckService) {
    stateAccessCheckService.registerStateRules([FiStateNames.Agreement.List, FiStateNames.Agreement.View._name], {
        SettingType: [ApplicationSettingsType.ConfidentialAgreement],
        AccessType: AccessRightType.Read,
    });

    stateAccessCheckService.registerStateRules(
        [
            FiStateNames.Agreement.View.Accepted.List,
            FiStateNames.Agreement.View.Accepted.View,
            FiStateNames.Agreement.View.Version,
            FiStateNames.Agreement.Edit,
            FiStateNames.Agreement.Create, //todo why we have no create state?
        ],
        {
            SettingType: [ApplicationSettingsType.ConfidentialAgreement],
            AccessType: AccessRightType.Admin,
        },
    );
}
